import { useState } from "react";
import { Button, Input } from "antd";
import { apiRequest, useAccount } from "@vojomo-core/shared";
import { User } from "@vojomo-core/shared/src/types/dbTypes";
import BlankLayout from "../../layouts/BlankLayout";

interface BusinessSignupFormProps {
  onSuccess: (companyName: string, updatedUser: User) => void;
  autoFocus?: boolean;
}

export const BusinessSignupForm = ({
  onSuccess,
  autoFocus = true
}: BusinessSignupFormProps): React.ReactElement => {
  const [companyName, setCompanyName] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    const trimmed = companyName.trim();
    if (!trimmed) {
      setError("Please enter a business name.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const updatedUser = await apiRequest<User>({
        method: "POST",
        subRoute: "user",
        path: "/setup_company",
        body: { company_name: trimmed }
      });
      onSuccess(trimmed, updatedUser);
    } catch (err: unknown) {
      const message = (err as Error)?.message ?? "";
      if (message.includes("status: 409")) {
        setError(
          "A business with that name already exists. Please try a different name."
        );
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mb-4">
        <Input
          size="large"
          placeholder="Business name"
          value={companyName}
          onChange={(e) => {
            setCompanyName(e.target.value);
            if (error) setError("");
          }}
          onPressEnter={handleSubmit}
          status={error ? "error" : ""}
          autoFocus={autoFocus}
        />
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>

      <Button
        type="primary"
        size="large"
        block
        loading={loading}
        onClick={handleSubmit}
      >
        Continue
      </Button>
    </>
  );
};

export const BusinessSignup = (): React.ReactElement => {
  const { setVojomoAccount } = useAccount();

  return (
    <BlankLayout pageTitle="Set up your business" contactFooter={false}>
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Set up your business
          </h2>
          <p className="text-gray-500 mb-6">
            Enter your business name to get started.
          </p>
          <BusinessSignupForm
            onSuccess={(_, updatedUser) => setVojomoAccount(updatedUser)}
          />
        </div>
      </div>
    </BlankLayout>
  );
};
